import React, { FC, useEffect, useState } from "react";
import { useSnackbar } from "notistack";
import { AxiosError } from "axios";
import { useTranslation } from "react-i18next";

import { ConfirmationModal } from "@components/common";
import { useSendDataSetToCsob } from "@api";
import { Checkbox, FormControlLabel } from "@components/base";
import { useUserStore } from "@store/user";

interface ShareDataModal {
  open: boolean;
  onClose: () => void;
}

const ShareDataModal: FC<ShareDataModal> = ({ open, onClose }) => {
  const { t } = useTranslation();
  const [shareCheckboxChecked, setShareCheckboxChecked] = useState<boolean>(false);
  const { user } = useUserStore();
  const {
    sendDataSetToCsob,
    sendDataSetToCsobEnabled,
    sendDataSetToCsobError,
    isPendingSendDataSetToCsob,
  } = useSendDataSetToCsob(user);
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    if (sendDataSetToCsobError) {
      const axiosError = sendDataSetToCsobError as AxiosError<{ message: string }>;
      const errorCode = axiosError.response?.data?.message || "unknown_error";

      enqueueSnackbar(t(`csob.questionnaires.errors.${errorCode}`), {
        variant: "error",
      });
    }
  }, [sendDataSetToCsobError]);

  return (
    sendDataSetToCsobEnabled && (
      <ConfirmationModal
        open={open}
        onClose={() => {
          onClose();
          setShareCheckboxChecked(false);
        }}
        onConfirm={() => {
          sendDataSetToCsob(undefined, {
            onSuccess: () => {
              onClose();
              setShareCheckboxChecked(false);
              enqueueSnackbar(t("snackbar.message.groupDataAddedToCsob"), {
                variant: "success",
              });
            },
            onError: (error) => {
              const axiosError = error as AxiosError<{ message: string }>;
              const errorCode =
                axiosError.response?.data?.message || "unknown_error";

              onClose();
              setShareCheckboxChecked(false);
              enqueueSnackbar(t(`csob.questionnaires.errors.${errorCode}`), {
                variant: "error",
              });
            },
          });
        }}
        title={t("csob.questionnaires.confirmShare.title")}
        description={
          <FormControlLabel
            control={
              <Checkbox
                checked={shareCheckboxChecked}
                onChange={(e) => setShareCheckboxChecked(e.target.checked)}
                inputProps={{
                  "aria-label": t("csob.questionnaires.confirmShare.checkboxText"),
                }}
              />
            }
            label={t("csob.questionnaires.confirmShare.checkboxText")}
          />
        }
        confirmText={t("csob.questionnaires.confirmShare.continue")}
        confirmDisabled={!shareCheckboxChecked || isPendingSendDataSetToCsob}
        noCancel={isPendingSendDataSetToCsob}
        isLoading={isPendingSendDataSetToCsob}
      />
    )
  );
};

export default ShareDataModal;
